import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Copy, Check, Shield, Key, Server, Eye, RefreshCw, Edit2, Save, X, Wifi, Sparkles, Zap, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TenantVPNManager = () => {
  const [vpnUsers, setVpnUsers] = useState([]);
  const [vpnLimit, setVpnLimit] = useState({ max: 10, remaining: 10 });
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [routerName, setRouterName] = useState('');
  const [ontSubnet, setOntSubnet] = useState('');
  const [editingOntSubnet, setEditingOntSubnet] = useState(null);
  const [newOntSubnetValue, setNewOntSubnetValue] = useState('');
  const [autoMigrateFramedIp, setAutoMigrateFramedIp] = useState(false);
  const [onboardingOnt, setOnboardingOnt] = useState(null);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [copiedField, setCopiedField] = useState('');
  const [viewedCredentials, setViewedCredentials] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingIp, setEditingIp] = useState(null);
  const [newIpValue, setNewIpValue] = useState('');
  const [editingRouter, setEditingRouter] = useState(null);
  const [newRouterValue, setNewRouterValue] = useState('');
  // Overlap modal state (2026-09-07): replaces window.confirm for subnet-overlap force decision
  const [overlapModal, setOverlapModal] = useState(null); // { vpnUsername, newSubnet, conflicts[] }
  const [overlapForcing, setOverlapForcing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | connected | disconnected
  const [autoFilling, setAutoFilling] = useState(null); // username being auto-filled
  const [autoFillingAll, setAutoFillingAll] = useState(false);
  const [bulkResultModal, setBulkResultModal] = useState(null);

  useEffect(() => {
    fetchVPNUsers();
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('[TenantVPN] Fetching tenants, token present:', !!token);
      
      // Use new endpoint for VPN tenant list
      const response = await axios.get(`${API_URL}/api/v2/tenants-for-vpn`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('[TenantVPN] API response:', response.data);
      
      if (response.data.success) {
        const tenantsData = response.data.data || [];
        console.log('[TenantVPN] Setting tenants:', tenantsData.length, 'tenants');
        setTenants(tenantsData);
      }
    } catch (error) {
      console.error('[TenantVPN] Error fetching tenants:', error);
      console.error('[TenantVPN] Error response:', error.response?.data);
      toast.error('Gagal memuat daftar tenant');
    }
  };

  const fetchVPNUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v2/tenant-vpn/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const data = response.data.data || {};
        setVpnUsers(data.users || []);
        setVpnLimit({
          max: data.max_limit || 10,
          remaining: data.remaining || 0
        });
      }
    } catch (error) {
      console.error('Error fetching VPN users:', error);
      toast.error('Gagal memuat daftar VPN users');
    } finally {
      setLoading(false);
    }
  };

  // Option B (2026-09-01): update ont_subnet + instant apply route
  const updateVpnOntSubnet = async (vpnUsername, newSubnet, autoMigrate = false) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API_URL}/api/v2/tenant-vpn/update-ont-subnet/${encodeURIComponent(vpnUsername)}`,
        { ont_subnet: newSubnet || '', auto_migrate_framed_ip: autoMigrate },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      if (response.data.success) {
        const applied = response.data.data?.route_applied_now;
        const migrate = response.data.data?.auto_migrate;
        toast.success(
          `Subnet ONT ${newSubnet ? 'diupdate ke ' + newSubnet : 'dihapus'} untuk ${vpnUsername}` +
          (newSubnet ? (applied ? ' - route langsung aktif' : ' - route menunggu L2TP connect') : '')
        );
        if (migrate && migrate.ok) {
          toast.success(
            `Auto-migrate: ${migrate.migrated_framed_ip} Framed-IP + ${migrate.migrated_current_ip} current_ip + ${migrate.kicked_sessions} session di-kick. Backup: ${migrate.backup_table}`,
            { duration: 8000 }
          );
        } else if (migrate && !migrate.ok && migrate.skipped_reason) {
          toast.warning(`Auto-migrate skipped: ${migrate.skipped_reason}`, { duration: 6000 });
        }
        setEditingOntSubnet(null);
        setNewOntSubnetValue('');
        setAutoMigrateFramedIp(false);
        fetchVPNUsers();
      } else {
        toast.error(response.data.message || 'Gagal update subnet');
      }
    } catch (error) {
      console.error('Error update ont_subnet:', error);
      // Handle overlap conflict (HTTP 409): open a proper modal so admin
      // can see WHO/WHICH router will lose route before deciding to force.
      if (error.response?.status === 409) {
        const detail = error.response.data.detail || {};
        setOverlapModal({
          vpnUsername,
          newSubnet: newSubnet || '',
          conflicts: detail.conflicts || [],
        });
        return;
      }
      toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || 'Gagal update subnet ONT');
    }
  };

  // Force override handler (called when admin confirms in overlap modal)
  const forceOverlapUpdate = async () => {
    if (!overlapModal) return;
    setOverlapForcing(true);
    try {
      const token = localStorage.getItem('token');
      const rf = await axios.patch(
        API_URL + '/api/v2/tenant-vpn/update-ont-subnet/' + encodeURIComponent(overlapModal.vpnUsername),
        { ont_subnet: overlapModal.newSubnet || '', force_overlap: true },
        { headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' } }
      );
      if (rf.data.success) {
        toast.success('Subnet di-force update. ' + (rf.data.warning || ''), { duration: 6000 });
        setEditingOntSubnet(null);
        setNewOntSubnetValue('');
        setOverlapModal(null);
        fetchVPNUsers();
      }
    } catch (e2) {
      toast.error(e2.response?.data?.detail?.message || 'Force update gagal');
    } finally {
      setOverlapForcing(false);
    }
  };

  const onboardRemoteOnt = async (vpnUsername) => {
    setOnboardingOnt(vpnUsername);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/v2/tenant-vpn/onboard-remote-ont/${encodeURIComponent(vpnUsername)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success(response.data.message || 'Route Remote ONT diaktifkan');
      } else {
        toast.error(response.data.message || 'Gagal onboard - pastikan L2TP MikroTik connected');
      }
    } catch (error) {
      console.error('Error onboard:', error);
      toast.error(error.response?.data?.detail || 'Gagal onboard Remote ONT');
    } finally {
      setOnboardingOnt(null);
    }
  };

  // Auto-Fill ONT Subnet from subnet_bank for a single VPN user (2026-02-25)
  const autoFillOntSubnet = async (vpnUsername, force = false) => {
    setAutoFilling(vpnUsername);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/v2/tenant-vpn/auto-fill-ont-subnet/${encodeURIComponent(vpnUsername)}`,
        { force_overlap: true, force: force },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      if (response.data.success) {
        const subs = response.data.data?.ont_subnets || [];
        const added = response.data.data?.routes_added || [];
        toast.success(
          `Auto-fill sukses: ${subs.length} subnet dari subnet_bank${added.length ? ` — ${added.length} route aktif` : ''}`,
          { duration: 5000 }
        );
        fetchVPNUsers();
      } else {
        toast.error(response.data.message || 'Auto-fill gagal');
      }
    } catch (error) {
      console.error('Error auto-fill:', error);
      const detail = error.response?.data?.detail;
      if (error.response?.status === 404 && detail?.suggestions?.length) {
        const s = detail.suggestions.map(x => x.name).join(', ');
        toast.error(
          `Router "${detail.router_name}" tidak ditemukan. Mungkin maksud Anda: ${s}. Klik nama router untuk rename.`,
          { duration: 8000 }
        );
      } else if (error.response?.status === 404) {
        toast.error(detail?.message || detail || 'Router belum punya subnet - pelanggan pertama harus reconnect PPPoE dulu', { duration: 6000 });
      } else if (error.response?.status === 409) {
        if (window.confirm(`${detail?.message || 'VPN ini bukan primary untuk router-nya.'}\n\nTetap paksa override?`)) {
          return autoFillOntSubnet(vpnUsername, true);
        }
      } else {
        toast.error(detail?.message || detail || 'Auto-fill gagal');
      }
    } finally {
      setAutoFilling(null);
    }
  };

  // Bulk Auto-Fill: pick primary VPN per router and auto-fill all in one call (2026-02-25)
  const autoFillAllOntSubnets = async () => {
    if (!window.confirm(
      'Auto-Fill Semua ONT Subnet?\n\n' +
      'Sistem akan mengambil daftar subnet dari subnet_bank dan mengisi ke setiap VPN "primary" per router.\n' +
      'VPN sekunder (VPN2, VPN3) akan di-skip untuk mencegah split-route.\n' +
      'Route kernel juga akan otomatis di-refresh.\n\n' +
      'Lanjutkan?'
    )) return;

    setAutoFillingAll(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/v2/tenant-vpn/auto-fill-ont-subnet-all`,
        {},
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      const data = response.data?.data || {};
      const u = (data.updated || []).length;
      const s = (data.skipped || []).length;
      const f = (data.failed || []).length;
      toast.success(`Selesai: ${u} updated, ${s} skipped, ${f} failed. Detail di modal.`, { duration: 5000 });
      setBulkResultModal(data);
      fetchVPNUsers();
    } catch (error) {
      console.error('Error bulk auto-fill:', error);
      toast.error(error.response?.data?.detail || 'Bulk auto-fill gagal');
    } finally {
      setAutoFillingAll(false);
    }
  };

  const createVPN = async () => {
    if (!selectedTenant) {
      toast.error('Pilih tenant terlebih dahulu');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/v2/tenant-vpn/create`,
        {
          tenant_name: selectedTenant.tenant_name || selectedTenant.company_name || selectedTenant.name,
          tenant_id: selectedTenant.uuid || selectedTenant.id || selectedTenant.tenant_id,
          router_name: routerName || '',
          ont_subnet: ontSubnet || undefined
        },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setCreatedCredentials(response.data.data);
        toast.success('VPN credentials berhasil dibuat!');
        setShowCreateForm(false);
        setRouterName('');
        setOntSubnet('');
        fetchVPNUsers();
      }
    } catch (error) {
      console.error('Error creating VPN:', error);
      toast.error(error.response?.data?.detail || 'Gagal membuat VPN credentials');
    } finally {
      setLoading(false);
    }
  };

  const deleteVPN = async (username) => {
    if (!window.confirm(`Hapus VPN user: ${username}?`)) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API_URL}/api/v2/tenant-vpn/delete/${username}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast.success(`VPN user ${username} berhasil dihapus`);
        // Refresh data from server
        fetchVPNUsers();
      } else {
        toast.error(response.data.message || 'Gagal menghapus VPN user');
      }
    } catch (error) {
      console.error('Error deleting VPN:', error);
      toast.error(error.response?.data?.detail || 'Gagal menghapus VPN user');
    }
  };

  const viewCredentials = async (username) => {
    // Clear previous data first to force re-render
    setViewedCredentials(null);
    setShowViewModal(false);
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/v2/tenant-vpn/credentials/${username}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        // Small delay to ensure state is cleared
        setTimeout(() => {
          setViewedCredentials(response.data.data);
          setShowViewModal(true);
        }, 100);
        toast.success('Credentials loaded!');
      }
    } catch (error) {
      console.error('Error viewing credentials:', error);
      toast.error('Gagal memuat credentials');
    } finally {
      setLoading(false);
    }
  };

  const replaceVPN = async (username) => {
    if (!window.confirm(`Replace VPN ${username}?\n\nVPN lama akan dihapus dan VPN baru akan dibuat dengan credentials berbeda.`)) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/v2/tenant-vpn/replace/${username}`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setCreatedCredentials(response.data.data);
        toast.success(`VPN replaced! Old: ${response.data.data.old_username}, New: ${response.data.data.vpn_username}`);
        fetchVPNUsers();
      }
    } catch (error) {
      console.error('Error replacing VPN:', error);
      toast.error(error.response?.data?.detail || 'Gagal replace VPN');
    } finally {
      setLoading(false);
    }
  };

  const resyncVPN = async (username) => {
    if (!window.confirm(`Re-sync VPN ${username}?\n\nCredential dari database akan di-sync ulang ke server RADIUS.`)) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/v2/tenant-vpn/resync/${username}`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast.success(response.data.message || `VPN ${username} berhasil di-sync!`);
      } else {
        toast.warning(response.data.message || 'Sync gagal - cek koneksi SSH ke server RADIUS');
      }
    } catch (error) {
      console.error('Error re-syncing VPN:', error);
      toast.error(error.response?.data?.detail || 'Gagal re-sync VPN');
    } finally {
      setLoading(false);
    }
  };

  const updateVpnIp = async (username, newIp) => {
    if (!newIp || !newIp.match(/^10\.10\.10\.\d{1,3}$/)) {
      toast.error("IP harus dalam format 10.10.10.x");
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.patch(
        `${API_URL}/api/v2/tenant-vpn/update-ip/${username}`,
        { local_ip: newIp },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success(response.data.message);
        setEditingIp(null);
        setNewIpValue("");
        fetchVPNUsers();
      }
    } catch (error) {
      console.error("Error updating VPN IP:", error);
      toast.error(error.response?.data?.detail || "Gagal update IP");
    } finally {
      setLoading(false);
    }
  };


  const updateRouterName = async (username, newName) => {
    if (!newName || newName.trim() === '') {
      toast.error("Nama router tidak boleh kosong");
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.patch(
        `${API_URL}/api/v2/tenant-vpn/update-router/${username}`,
        { router_name: newName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success(response.data.message);
        setEditingRouter(null);
        setNewRouterValue("");
        fetchVPNUsers();
      }
    } catch (error) {
      console.error("Error updating router name:", error);
      toast.error(error.response?.data?.detail || "Gagal update nama router");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, field) => {
    // Try modern clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        setCopiedField(field);
        toast.success(`${field} disalin!`);
        setTimeout(() => setCopiedField(''), 2000);
      }).catch(() => {
        // Fallback to textarea method
        fallbackCopy(text, field);
      });
    } else {
      // Fallback for older browsers or non-secure context
      fallbackCopy(text, field);
    }
  };

  const fallbackCopy = (text, field) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      setCopiedField(field);
      toast.success(`${field} disalin! (Manual select jika gagal)`);
      setTimeout(() => setCopiedField(''), 2000);
    } catch (err) {
      toast.info(`Silakan select text dan copy manual (Ctrl+C)`);
    }
    document.body.removeChild(textArea);
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">
            Tenant VPN Management
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            Kelola L2TP VPN credentials untuk tenant
          </p>
        </div>
        <Button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="bg-blue-500 hover:bg-blue-600 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create VPN
        </Button>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <Card className="border-blue-200 dark:border-blue-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Buat VPN Credentials Baru</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {tenants.length > 0 ? (
              <div>
                <label className="text-sm font-medium mb-2 block">Pilih Tenant</label>
                <select
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                  value={selectedTenant?.uuid || selectedTenant?.tenant_id || selectedTenant?.id || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === 'manual') {
                      setSelectedTenant({ manual: true });
                    } else {
                      const tenant = tenants.find(t => (t.uuid || t.tenant_id || t.id) == value);
                      setSelectedTenant(tenant);
                    }
                  }}
                >
                  <option value="">-- Pilih Tenant --</option>
                  {tenants.map((tenant) => (
                    <option key={tenant.uuid || tenant.tenant_id || tenant.id} value={tenant.uuid || tenant.tenant_id || tenant.id}>
                      {tenant.tenant_name || tenant.company_name || tenant.name} ({tenant.tenant_code || tenant.uuid})
                    </option>
                  ))}
                  <option value="manual">➕ Input Manual</option>
                </select>
                {selectedTenant && !selectedTenant.manual && (
                  <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-sm">
                    <p className="text-slate-700 dark:text-slate-300">
                      <strong>Tenant:</strong> {selectedTenant.tenant_name || selectedTenant.company_name || selectedTenant.name}
                    </p>
                    <p className="text-slate-700 dark:text-slate-300">
                      <strong>ID:</strong> {selectedTenant.uuid || selectedTenant.tenant_id || selectedTenant.id}
                    </p>
                    {selectedTenant.subscription_plan && (
                      <p className="text-slate-700 dark:text-slate-300">
                        <strong>Max VPN:</strong> {selectedTenant.subscription_plan.max_vpn || selectedTenant.subscription_plan.max_mikrotiks || selectedTenant.subscription_plan.max_nas_vpn || 0}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : null}
            
            {(tenants.length === 0 || selectedTenant?.manual) && (
              <>
                <div>
                  <label className="text-sm font-medium mb-2 block">Tenant Name</label>
                  <Input
                    placeholder="Contoh: PT Pusaka Kreasi Mandiri"
                    value={selectedTenant?.company_name || selectedTenant?.name || ''}
                    onChange={(e) => setSelectedTenant({ 
                      ...selectedTenant, 
                      company_name: e.target.value,
                      name: e.target.value,
                      manual: true 
                    })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Tenant ID</label>
                  <Input
                    placeholder="Contoh: pusaka001"
                    value={selectedTenant?.tenant_id || selectedTenant?.id || ''}
                    onChange={(e) => setSelectedTenant({ 
                      ...selectedTenant, 
                      tenant_id: e.target.value,
                      id: e.target.value,
                      manual: true 
                    })}
                  />
                </div>
              </>
            )}
            
            {/* Router Name Input */}
            <div>
              <label className="text-sm font-medium mb-2 block">Nama Router (Opsional)</label>
              <Input
                placeholder="Contoh: PAMULANG RB1100AHX4"
                value={routerName}
                onChange={(e) => setRouterName(e.target.value)}
              />
              <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">Nama router untuk identifikasi VPN di daftar</p>
            </div>

            {/* Subnet ONT (opsional) untuk Remote ONT auto-route */}
            <div>
              <label className="text-sm font-medium mb-2 block">Subnet ONT (Opsional, untuk Remote ONT)</label>
              <Input
                placeholder="172.16.31.0/24  atau  172.16.17.0/24, 172.16.18.0/24"
                value={ontSubnet}
                onChange={(e) => setOntSubnet(e.target.value)}
                data-testid="vpn-ont-subnet-input"
              />
              <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">Format CIDR - support multi-subnet dipisah koma (contoh: <code className="font-mono">172.16.17.0/24, 172.16.18.0/24</code>). Setelah L2TP connect sistem otomatis route semua subnet via tunnel untuk Remote ONT. Catatan: 1 subnet hanya boleh dipegang 1 router - overlap akan direject (kecuali force).</p>
            </div>

            <div className="flex space-x-2">
              <Button
                onClick={createVPN}
                disabled={loading}
                className="bg-green-500 hover:bg-green-600 text-white"
              >
                <Key className="w-4 h-4 mr-2" />
                Generate Credentials
              </Button>
              <Button
                onClick={() => setShowCreateForm(false)}
                variant="outline"
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Created Credentials Display */}
      {createdCredentials && (
        <Card className="border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20" data-testid="vpn-created-credentials">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center text-green-700 dark:text-green-300">
              <Check className="w-5 h-5 mr-2" />
              VPN Berhasil Dibuat — {createdCredentials.tenant_name || createdCredentials.vpn_username}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">

            {/* HERO: 1-Click Setup Script — this is the primary action */}
            <div className="rounded-xl border-2 border-blue-500 dark:border-blue-400 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950/40 dark:to-cyan-950/40 p-4 shadow-md">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h3 className="text-lg font-bold text-blue-900 dark:text-blue-100 flex items-center gap-2">
                    <span>🚀</span> Setup MikroTik — 1 Klik
                  </h3>
                  <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                    Copy script lengkap, lalu paste ke <strong>Winbox → New Terminal</strong> router MikroTik anda.
                    Script sudah termasuk L2TP VPN 1 + VPN 2 + RADIUS Primary + RADIUS Backup HA.
                  </p>
                </div>
              </div>

              {/* Big Copy Button */}
              <Button
                size="lg"
                data-testid="mikrotik-setup-copy-all-btn"
                onClick={() => copyToClipboard(createdCredentials.mikrotik_setup || createdCredentials.mikrotik_script || '', 'FullScript')}
                className={`w-full h-14 text-base font-semibold shadow-lg transition-all ${copiedField === 'FullScript' ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'} text-white`}
              >
                {copiedField === 'FullScript' ? (
                  <><Check className="w-5 h-5 mr-2" /> Tersalin! Silakan paste di MikroTik Terminal</>
                ) : (
                  <><Copy className="w-5 h-5 mr-2" /> Copy Script Lengkap (Klik untuk salin ke clipboard)</>
                )}
              </Button>

              {/* Step-by-step guide */}
              <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-2 text-xs">
                <div className="flex items-start gap-2 p-2 bg-white dark:bg-slate-800 rounded" data-testid="setup-step-1">
                  <span className="flex-none w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center text-[10px] font-bold">1</span>
                  <span className="text-slate-700 dark:text-slate-300">Klik <strong>Copy</strong> di atas</span>
                </div>
                <div className="flex items-start gap-2 p-2 bg-white dark:bg-slate-800 rounded" data-testid="setup-step-2">
                  <span className="flex-none w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center text-[10px] font-bold">2</span>
                  <span className="text-slate-700 dark:text-slate-300">Buka Winbox → <strong>New Terminal</strong></span>
                </div>
                <div className="flex items-start gap-2 p-2 bg-white dark:bg-slate-800 rounded" data-testid="setup-step-3">
                  <span className="flex-none w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center text-[10px] font-bold">3</span>
                  <span className="text-slate-700 dark:text-slate-300"><strong>Paste</strong> (Ctrl+V) &amp; Enter</span>
                </div>
                <div className="flex items-start gap-2 p-2 bg-white dark:bg-slate-800 rounded" data-testid="setup-step-4">
                  <span className="flex-none w-5 h-5 rounded-full bg-green-600 text-white flex items-center justify-center text-[10px] font-bold">4</span>
                  <span className="text-slate-700 dark:text-slate-300">Verify: <code className="text-[10px] bg-slate-100 dark:bg-slate-700 px-1 rounded">/radius print</code> harus tampil <strong>2 baris</strong></span>
                </div>
              </div>

              {/* Preview of script */}
              <details className="mt-3" data-testid="setup-script-preview">
                <summary className="text-xs text-blue-700 dark:text-blue-300 cursor-pointer hover:underline select-none">
                  📄 Lihat / edit script sebelum paste (opsional)
                </summary>
                <pre className="mt-2 font-mono text-[11px] leading-relaxed whitespace-pre-wrap bg-slate-900 text-green-400 p-3 rounded-lg overflow-x-auto max-h-96">
{createdCredentials.mikrotik_setup || createdCredentials.mikrotik_script}
                </pre>
              </details>
            </div>

            {/* Credentials Details — Collapsible (secondary info) */}
            <details className="rounded-lg border border-slate-200 dark:border-slate-700" data-testid="vpn-credentials-details">
              <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg flex items-center gap-2">
                <Key className="w-4 h-4" /> Detail credentials individual (jika butuh manual setup)
              </summary>
              <div className="p-3 space-y-3">
                <div className="p-3 bg-white dark:bg-slate-800 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">VPN 1 Server:</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid="copy-vpn-server"
                      onClick={() => copyToClipboard(createdCredentials.vpn_server_ip || createdCredentials.vpn_server, 'Server')}
                    >
                      {copiedField === 'Server' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    </Button>
                  </div>
                  <p className="font-mono text-sm">{createdCredentials.vpn_server_ip || createdCredentials.vpn_server}</p>
                </div>

                <div className="p-3 bg-white dark:bg-slate-800 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">VPN 1 Username:</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid="copy-vpn-username"
                      onClick={() => copyToClipboard(createdCredentials.vpn_username, 'Username')}
                    >
                      {copiedField === 'Username' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    </Button>
                  </div>
                  <p className="font-mono text-sm">{createdCredentials.vpn_username}</p>
                </div>

                <div className="p-3 bg-white dark:bg-slate-800 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">VPN 1 Password:</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid="copy-vpn-password"
                      onClick={() => copyToClipboard(createdCredentials.vpn_password, 'Password')}
                    >
                      {copiedField === 'Password' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    </Button>
                  </div>
                  <p className="font-mono text-sm">{createdCredentials.vpn_password}</p>
                </div>

                {createdCredentials.radius_secret && (
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">RADIUS Secret (untuk kedua RADIUS entry):</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid="copy-radius-secret"
                        onClick={() => copyToClipboard(createdCredentials.radius_secret, 'RadiusSecret')}
                      >
                        {copiedField === 'RadiusSecret' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      </Button>
                    </div>
                    <p className="font-mono text-sm break-all">{createdCredentials.radius_secret}</p>
                  </div>
                )}

                {createdCredentials.has_backup_vpn && (
                  <div className="border-2 border-blue-300 dark:border-blue-700 rounded-lg p-3 bg-blue-50 dark:bg-blue-900/20">
                    <div className="flex items-center mb-2">
                      <span className="text-xs font-bold text-blue-700 dark:text-blue-300">
                        🛡️ VPN 2 BACKUP (HA Failover — VM 101)
                      </span>
                    </div>
                    <p className="text-xs text-blue-600 dark:text-blue-400 mb-3">
                      Kredensial cadangan otomatis aktif saat Server 1 mati. Sudah termasuk di Script Lengkap di atas.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div className="p-2 bg-white dark:bg-slate-800 rounded" data-testid="backup-vpn-server">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs text-slate-500">Backup Server:</span>
                          <Button size="sm" variant="ghost" className="h-6 px-2"
                            onClick={() => copyToClipboard(createdCredentials.backup_vpn_server_ip, 'BackupServer')}>
                            {copiedField === 'BackupServer' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          </Button>
                        </div>
                        <p className="font-mono text-xs">{createdCredentials.backup_vpn_server_ip}</p>
                      </div>
                      <div className="p-2 bg-white dark:bg-slate-800 rounded" data-testid="backup-vpn-client-ip">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs text-slate-500">Backup Client IP:</span>
                          <Button size="sm" variant="ghost" className="h-6 px-2"
                            onClick={() => copyToClipboard(createdCredentials.backup_vpn_client_ip, 'BackupClientIP')}>
                            {copiedField === 'BackupClientIP' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          </Button>
                        </div>
                        <p className="font-mono text-xs">{createdCredentials.backup_vpn_client_ip}</p>
                      </div>
                      <div className="p-2 bg-white dark:bg-slate-800 rounded" data-testid="backup-vpn-username">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs text-slate-500">Backup Username:</span>
                          <Button size="sm" variant="ghost" className="h-6 px-2"
                            onClick={() => copyToClipboard(createdCredentials.backup_vpn_username, 'BackupUser')}>
                            {copiedField === 'BackupUser' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          </Button>
                        </div>
                        <p className="font-mono text-xs">{createdCredentials.backup_vpn_username}</p>
                      </div>
                      <div className="p-2 bg-white dark:bg-slate-800 rounded" data-testid="backup-vpn-password">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs text-slate-500">Backup Password:</span>
                          <Button size="sm" variant="ghost" className="h-6 px-2"
                            onClick={() => copyToClipboard(createdCredentials.backup_vpn_password, 'BackupPass')}>
                            {copiedField === 'BackupPass' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          </Button>
                        </div>
                        <p className="font-mono text-xs">{createdCredentials.backup_vpn_password}</p>
                      </div>
                      <div className="p-2 bg-white dark:bg-slate-800 rounded col-span-full" data-testid="backup-vpn-psk">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs text-slate-500">IPsec PSK (untuk L2TP #2 dengan enkripsi):</span>
                          <Button size="sm" variant="ghost" className="h-6 px-2"
                            onClick={() => copyToClipboard(createdCredentials.backup_ipsec_psk, 'IPsecPSK')}>
                            {copiedField === 'IPsecPSK' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          </Button>
                        </div>
                        <p className="font-mono text-xs">{createdCredentials.backup_ipsec_psk}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </details>

            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 p-3 rounded-lg">
              <p className="text-xs text-yellow-800 dark:text-yellow-300">
                ⚠️ <strong>PENTING:</strong> Simpan credentials ini dengan aman! Setelah tutup, gunakan tombol <Eye className="w-3 h-3 inline" /> di list untuk melihat kembali.
              </p>
            </div>

            <Button
              onClick={() => setCreatedCredentials(null)}
              variant="outline"
              className="w-full"
              data-testid="vpn-close-credentials-btn"
            >
              Tutup
            </Button>
          </CardContent>
        </Card>
      )}

      {/* VPN Users List */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-sm sm:text-base flex items-center flex-shrink-0">
              <Server className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400 flex-shrink-0" />
              <span>Active VPN Users ({vpnUsers.length}/{vpnLimit.max}) - Sisa: {vpnLimit.remaining}</span>
            </CardTitle>
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center w-full sm:w-auto">
              <div className="relative w-full sm:w-auto">
                <Input
                  type="text"
                  placeholder="Cari router / username / IP / tenant..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-8 text-xs w-full sm:w-72 pr-8"
                  data-testid="vpn-search-input"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    data-testid="vpn-search-clear-btn"
                    aria-label="Clear search"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="h-8 text-xs px-2 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 w-full sm:w-auto"
                data-testid="vpn-status-filter"
              >
                <option value="all">Semua Status</option>
                <option value="connected">Connected</option>
                <option value="disconnected">Disconnected</option>
              </select>
              <Button
                size="sm"
                onClick={autoFillAllOntSubnets}
                disabled={autoFillingAll}
                className="h-8 text-xs bg-cyan-500 hover:bg-cyan-600 text-white gap-1.5 w-full sm:w-auto justify-center"
                title="Auto-Fill ONT Subnet untuk semua VPN primary dari subnet_bank"
                data-testid="vpn-autofill-all-btn"
              >
                {autoFillingAll ? (
                  <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Auto-Fill...</>
                ) : (
                  <><Zap className="w-3.5 h-3.5" /> Auto-Fill Semua</>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400">Loading...</div>
          ) : vpnUsers.length === 0 ? (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400">
              Belum ada VPN users. Klik "Create VPN" untuk membuat yang pertama.
            </div>
          ) : (() => {
            const q = searchQuery.trim().toLowerCase();
            const filteredUsers = vpnUsers.filter((user) => {
              const uObj = typeof user === 'object' ? user : {};
              const uname = (typeof user === 'string' ? user : (uObj.vpn_username || uObj.username || '')).toLowerCase();
              const routerName = (uObj.router_name || uObj.display_name || '').toLowerCase();
              const tenantName = (uObj.tenant_name || '').toLowerCase();
              const localIp = (uObj.local_ip || '').toLowerCase();
              const serverIp = (uObj.vpn_server_ip || '').toLowerCase();
              const isConnected = uObj.is_connected === true || uObj.is_online === true;

              // Status filter
              if (statusFilter === 'connected' && !isConnected) return false;
              if (statusFilter === 'disconnected' && isConnected) return false;

              // Search filter
              if (!q) return true;
              return (
                uname.includes(q) ||
                routerName.includes(q) ||
                tenantName.includes(q) ||
                localIp.includes(q) ||
                serverIp.includes(q)
              );
            });

            if (filteredUsers.length === 0) {
              return (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400" data-testid="vpn-search-empty">
                  Tidak ada hasil untuk pencarian "<span className="font-medium">{searchQuery}</span>"
                  {statusFilter !== 'all' && <> dengan status <span className="font-medium">{statusFilter}</span></>}.
                </div>
              );
            }

            return (
              <>
                {(q || statusFilter !== 'all') && (
                  <div className="mb-3 text-xs text-slate-500 dark:text-slate-400" data-testid="vpn-search-result-count">
                    Menampilkan {filteredUsers.length} dari {vpnUsers.length} VPN users
                  </div>
                )}
                <div className="space-y-2">
                  {filteredUsers.map((user, index) => {
                const username = typeof user === 'string' ? user : (user.vpn_username || user.username);
                const routerNameDisplay = typeof user === 'object' ? (user.router_name || user.display_name || 'Unnamed Router') : '';
                const tenantName = typeof user === 'object' ? (user.tenant_name || '') : '';
                const localIp = typeof user === 'object' ? (user.local_ip || '') : '';
                const isOnline = typeof user === 'object' ? (user.is_connected === true || user.is_online === true) : false;
                const sessionIp = typeof user === 'object' ? (user.session_ip || '') : '';
                const uptime = typeof user === 'object' ? (user.uptime || '') : '';
                
                return (
                  <div
                    key={user.id || index}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg gap-2"
                  >
                    <div className="flex items-start sm:items-center gap-3 min-w-0 flex-1">
                      <div className="relative flex-shrink-0 mt-1 sm:mt-0">
                        <Shield className={isOnline ? "w-4 h-4 text-green-500" : "w-4 h-4 text-slate-400"} />
                        <span className={`absolute -top-1 -right-1 w-2 h-2 rounded-full ${isOnline ? 'bg-green-500 animate-pulse' : 'bg-slate-300'}`}></span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center flex-wrap gap-2">
                          {isOnline ? (
                            <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-1.5 py-0.5 rounded font-medium" data-testid="vpn-status-online">
                              ● Connected {sessionIp && `(${sessionIp})`} {uptime && `• ${uptime}`}
                            </span>
                          ) : (
                            <span className="text-xs bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 px-1.5 py-0.5 rounded" data-testid="vpn-status-offline">
                              ○ Disconnected
                            </span>
                          )}
                          {editingRouter === username ? (
                            <div className="flex items-center gap-1">
                              <input
                                type="text"
                                value={newRouterValue}
                                onChange={(e) => setNewRouterValue(e.target.value)}
                                className="w-32 text-sm px-2 py-0.5 border rounded"
                                placeholder="Nama Router"
                                autoFocus
                              />
                              <button
                                onClick={() => updateRouterName(username, newRouterValue)}
                                className="text-green-600 hover:text-green-800 p-1 dark:text-green-400"
                                title="Save"
                              >
                                <Save className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => { setEditingRouter(null); setNewRouterValue(""); }}
                                className="text-red-600 hover:text-red-800 p-1 dark:text-red-400"
                                title="Cancel"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ) : (
                            <span 
                              className="font-medium text-sm text-slate-900 dark:text-slate-100 cursor-pointer hover:text-blue-600"
                              onClick={() => { setEditingRouter(username); setNewRouterValue(routerNameDisplay); }}
                              title="Klik untuk edit nama router"
                            >
                              🌐 {routerNameDisplay}
                            </span>
                          )}
                          {localIp && (
                            editingIp === username ? (
                              <div className="flex items-center gap-1">
                                <input
                                  type="text"
                                  value={newIpValue}
                                  onChange={(e) => setNewIpValue(e.target.value)}
                                  className="w-24 text-xs px-2 py-0.5 border rounded"
                                  placeholder="10.10.10.x"
                                  autoFocus
                                />
                                <button
                                  onClick={() => updateVpnIp(username, newIpValue)}
                                  className="text-green-600 hover:text-green-800 p-1 dark:text-green-400"
                                  title="Save"
                                >
                                  <Save className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => { setEditingIp(null); setNewIpValue(""); }}
                                  className="text-red-600 hover:text-red-800 p-1 dark:text-red-400"
                                  title="Cancel"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ) : (
                              <span 
                                className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded cursor-pointer hover:bg-blue-200 dark:bg-blue-900/40 dark:text-blue-300" 
                                onClick={() => { setEditingIp(username); setNewIpValue(localIp); }} 
                                title="Click to edit"
                              >
                                {localIp}
                              </span>
                            )
                          )}
                          {/* Option B: ont_subnet inline edit + onboard button */}
                          {(() => {
                            const ontSub = (typeof user === 'object') ? (user.ont_subnet || '') : '';
                            const ontSubList = ontSub ? ontSub.split(',').map(s => s.trim()).filter(Boolean) : [];
                            const isEditing = editingOntSubnet === username;
                            if (isEditing) {
                              return (
                                <div className="flex items-center gap-1">
                                  <input
                                    type="text"
                                    value={newOntSubnetValue}
                                    onChange={(e) => setNewOntSubnetValue(e.target.value)}
                                    className="min-w-[280px] w-full max-w-[720px] text-xs px-2 py-0.5 border rounded font-mono dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600"
                                    placeholder="172.16.1.0/24, 172.16.2.0/24, ..."
                                    autoFocus
                                    data-testid={`vpn-edit-ont-subnet-${username}`}
                                  />
                                  <button
                                    onClick={() => updateVpnOntSubnet(username, newOntSubnetValue, autoMigrateFramedIp)}
                                    className={autoMigrateFramedIp ? "text-orange-600 hover:text-orange-800 p-1 dark:text-orange-400" : "text-green-600 hover:text-green-800 p-1 dark:text-green-400"}
                                    title={autoMigrateFramedIp ? "Save + Auto-migrate Framed-IP customer (backup dulu, lalu update radreply + kick sessions)" : "Save subnet (pisah dengan koma untuk multi-CIDR)"}
                                  >
                                    <Save className="w-3 h-3" />
                                  </button>
                                  <label className="flex items-center gap-0.5 text-[9px] cursor-pointer text-orange-600 dark:text-orange-400 select-none" title="Kalau ceklis: sistem otomatis update Framed-IP customer sesuai mapping subnet + kick sessions untuk reconnect. Backup dibuat otomatis.">
                                    <input type="checkbox" checked={autoMigrateFramedIp} onChange={(e) => setAutoMigrateFramedIp(e.target.checked)} className="w-3 h-3" data-testid={`vpn-auto-migrate-${username}`} />
                                    <span>auto-migrate</span>
                                  </label>
                                  <button
                                    onClick={() => { setEditingOntSubnet(null); setNewOntSubnetValue(""); }}
                                    className="text-red-600 hover:text-red-800 p-1 dark:text-red-400"
                                    title="Cancel"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                  <button
                                    onClick={() => autoFillOntSubnet(username)}
                                    disabled={autoFilling === username}
                                    className="text-cyan-600 hover:text-cyan-800 p-1 dark:text-cyan-400 disabled:opacity-50"
                                    title="Auto-Fill dari subnet_bank router ini"
                                    data-testid={`vpn-autofill-inline-${username}`}
                                  >
                                    {autoFilling === username ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                  </button>
                                </div>
                              );
                            }
                            // Display mode: badges for each subnet if multiple, single pill otherwise, or "+ ONT Subnet" placeholder
                            if (ontSubList.length > 1) {
                              return (
                                <span
                                  className="inline-flex items-center gap-1 flex-wrap cursor-pointer"
                                  onClick={() => { setEditingOntSubnet(username); setNewOntSubnetValue(ontSub); }}
                                  title={`Klik untuk edit ${ontSubList.length} subnet ONT`}
                                  data-testid={`vpn-ont-subnet-${username}`}
                                >
                                  <span className="text-xs bg-cyan-100 text-cyan-700 px-1.5 py-0.5 rounded font-medium hover:bg-cyan-200 dark:bg-cyan-900/40 dark:text-cyan-300">
                                    ONT ×{ontSubList.length}
                                  </span>
                                  {ontSubList.map((s) => (
                                    <span key={s} className="text-[10px] bg-cyan-50 text-cyan-600 px-1 py-0.5 rounded hover:bg-cyan-100 dark:bg-cyan-900/20 dark:text-cyan-400 font-mono">
                                      {s}
                                    </span>
                                  ))}
                                  <Edit2 className="w-3 h-3 text-cyan-500 hover:text-cyan-700 dark:text-cyan-400" data-testid={`vpn-edit-icon-${username}`} />
                                </span>
                              );
                            }
                            return (
                              <span
                                className={`text-xs px-2 py-0.5 rounded cursor-pointer ${ontSub ? 'bg-cyan-100 text-cyan-700 hover:bg-cyan-200 dark:bg-cyan-900/40 dark:text-cyan-300' : 'bg-slate-100 text-slate-500 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 border border-dashed border-slate-300 dark:border-slate-600'}`}
                                onClick={() => { setEditingOntSubnet(username); setNewOntSubnetValue(ontSub); }}
                                title={ontSub ? `Klik untuk edit subnet ONT (Remote ONT: ${ontSub})` : 'Set subnet ONT untuk aktifkan Remote ONT'}
                                data-testid={`vpn-ont-subnet-${username}`}
                              >
                                {ontSub ? `ONT: ${ontSub}` : '+ ONT Subnet'}
                                <Edit2 className="w-3 h-3 inline ml-1 opacity-70" />
                              </span>
                            );
                          })()}
                        </div>
                        <span className="font-mono text-sm text-slate-500 dark:text-slate-400">
                          {username}
                        </span>
                        {tenantName && (
                          <span className="text-xs text-slate-400 block dark:text-slate-500">
                            Tenant: {tenantName}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center flex-wrap justify-end gap-1 sm:flex-nowrap sm:space-x-1 sm:gap-0 self-end sm:self-auto">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => viewCredentials(username)}
                        className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                        title="View Credentials"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => resyncVPN(username)}
                        className="text-green-500 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/30"
                        title="Re-sync to Server"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onboardRemoteOnt(username)}
                        disabled={onboardingOnt === username}
                        className="text-cyan-500 hover:text-cyan-700 hover:bg-cyan-50 dark:hover:bg-cyan-900/30"
                        title="Onboard Remote ONT (apply route sekarang)"
                        data-testid={`vpn-onboard-${username}`}
                      >
                        {onboardingOnt === username ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Wifi className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => autoFillOntSubnet(username)}
                        disabled={autoFilling === username}
                        className="text-purple-500 hover:text-purple-700 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                        title="Auto-Fill ONT Subnet dari subnet_bank router ini"
                        data-testid={`vpn-autofill-${username}`}
                      >
                        {autoFilling === username ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Sparkles className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => replaceVPN(username)}
                        className="text-orange-500 hover:text-orange-700 hover:bg-orange-50 dark:hover:bg-orange-900/30"
                        title="Replace VPN (New Credentials)"
                      >
                        <Key className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteVPN(username)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30"
                        title="Delete VPN"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
                </div>
              </>
            );
          })()}
        </CardContent>
      </Card>

      {/* Bulk Auto-Fill Result Modal (2026-02-25) */}
      {bulkResultModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="vpn-bulk-autofill-modal">
          <Card className="w-full max-w-3xl max-h-[85vh] overflow-y-auto">
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Zap className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                  Auto-Fill Semua ONT Subnet — Hasil
                </CardTitle>
                <Button size="sm" variant="ghost" onClick={() => setBulkResultModal(null)} data-testid="vpn-bulk-modal-close">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <p className="text-xs text-green-700 dark:text-green-300">Updated</p>
                  <p className="text-2xl font-bold text-green-800 dark:text-green-200">{(bulkResultModal.updated || []).length}</p>
                </div>
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                  <p className="text-xs text-slate-600 dark:text-slate-400">Skipped</p>
                  <p className="text-2xl font-bold text-slate-700 dark:text-slate-300">{(bulkResultModal.skipped || []).length}</p>
                </div>
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                  <p className="text-xs text-red-700 dark:text-red-300">Failed</p>
                  <p className="text-2xl font-bold text-red-800 dark:text-red-200">{(bulkResultModal.failed || []).length}</p>
                </div>
              </div>

              {(bulkResultModal.updated || []).length > 0 && (
                <details open className="border rounded-lg p-3 bg-green-50/40 dark:bg-green-900/10">
                  <summary className="text-sm font-medium text-green-800 dark:text-green-300 cursor-pointer">
                    ✅ Updated ({bulkResultModal.updated.length})
                  </summary>
                  <div className="mt-3 space-y-2">
                    {bulkResultModal.updated.map((r) => (
                      <div key={r.username} className="text-xs p-2 bg-white dark:bg-slate-800 rounded border">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-mono font-semibold">{r.username}</span>
                          <span className="text-slate-500">{r.router_name}</span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {(r.subnets || []).map((s) => (
                            <span key={s} className="text-[10px] font-mono bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300 px-1.5 py-0.5 rounded">{s}</span>
                          ))}
                        </div>
                        {(r.routes_added || []).length > 0 && (
                          <div className="text-[10px] text-green-600 dark:text-green-400 mt-1">→ {r.routes_added.length} route aktif</div>
                        )}
                        {(r.routes_removed || []).length > 0 && (
                          <div className="text-[10px] text-amber-600 dark:text-amber-400 mt-1">← {r.routes_removed.length} route lama dihapus</div>
                        )}
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {(bulkResultModal.failed || []).length > 0 && (
                <details open className="border rounded-lg p-3 bg-red-50/40 dark:bg-red-900/10">
                  <summary className="text-sm font-medium text-red-800 dark:text-red-300 cursor-pointer">
                    ⚠️ Failed / Butuh Aksi Manual ({bulkResultModal.failed.length})
                  </summary>
                  <div className="mt-3 space-y-2">
                    {bulkResultModal.failed.map((r) => {
                      let err = r.error || '';
                      let msg = err;
                      let suggestions = [];
                      try {
                        const parsed = typeof err === 'string' && err.startsWith('{') ? JSON.parse(err.replace(/'/g, '"')) : (typeof err === 'object' ? err : null);
                        if (parsed) {
                          msg = parsed.message || err;
                          suggestions = parsed.suggestions || [];
                        }
                      } catch { /* fallback: use raw */ }
                      return (
                        <div key={r.username} className="text-xs p-2 bg-white dark:bg-slate-800 rounded border">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-mono font-semibold">{r.username}</span>
                            <span className="text-slate-500">{r.router_name}</span>
                          </div>
                          <p className="text-slate-600 dark:text-slate-400">{msg}</p>
                          {suggestions.length > 0 && (
                            <p className="mt-1 text-[11px] text-blue-600 dark:text-blue-400">
                              💡 Kandidat: {suggestions.map(s => s.name).join(', ')}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </details>
              )}

              {(bulkResultModal.skipped || []).length > 0 && (
                <details className="border rounded-lg p-3 bg-slate-50/40 dark:bg-slate-800/40">
                  <summary className="text-sm font-medium text-slate-700 dark:text-slate-300 cursor-pointer">
                    Skipped ({bulkResultModal.skipped.length})
                  </summary>
                  <div className="mt-3 space-y-1 text-xs">
                    {bulkResultModal.skipped.map((r, i) => (
                      <div key={i} className="p-2 bg-white dark:bg-slate-800 rounded border">
                        <span className="font-mono">{r.username}</span>
                        <span className="text-slate-500 ml-2">— {r.reason}</span>
                        {r.primary && <span className="text-slate-400 ml-2">(primary: {r.primary})</span>}
                      </div>
                    ))}
                  </div>
                </details>
              )}

              <Button variant="outline" className="w-full" onClick={() => setBulkResultModal(null)} data-testid="vpn-bulk-modal-close-bottom">
                Tutup
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* View Credentials Modal */}
      {showViewModal && viewedCredentials && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center">
                  <Eye className="w-5 h-5 mr-2 text-blue-500" />
                  VPN Credentials
                </CardTitle>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setShowViewModal(false);
                    setViewedCredentials(null);
                  }}
                >
                  ✕
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  <strong>Tenant:</strong> {viewedCredentials.tenant_name}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">VPN Server</label>
                <div className="flex items-center space-x-2">
                  <Input
                    readOnly
                    value={viewedCredentials.vpn_server_ip || viewedCredentials.vpn_server || '103.179.252.149'}
                    className="font-mono text-sm"
                  />
                  <Button
                    size="sm"
                    onClick={() => copyToClipboard(viewedCredentials.vpn_server_ip || viewedCredentials.vpn_server || '103.179.252.149', 'Server')}
                    className="bg-blue-500 hover:bg-blue-600"
                  >
                    {copiedField === 'Server' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">VPN Client IP (untuk NAS)</label>
                <div className="flex items-center space-x-2">
                  <Input
                    readOnly
                    value={viewedCredentials.vpn_client_ip || '10.10.10.x'}
                    className="font-mono text-sm bg-green-50 dark:bg-green-900/30"
                  />
                  <Button
                    size="sm"
                    onClick={() => copyToClipboard(viewedCredentials.vpn_client_ip || '10.10.10.x', 'VPN Client IP')}
                    className="bg-blue-500 hover:bg-blue-600"
                  >
                    {copiedField === 'VPN Client IP' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-green-600 mt-1 dark:text-green-400">✓ NAS otomatis tersinkron dengan IP ini</p>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">VPN Username</label>
                <div className="flex items-center space-x-2">
                  <Input
                    readOnly
                    value={viewedCredentials.vpn_username}
                    className="font-mono text-sm"
                  />
                  <Button
                    size="sm"
                    onClick={() => copyToClipboard(viewedCredentials.vpn_username, 'Username')}
                    className="bg-blue-500 hover:bg-blue-600"
                  >
                    {copiedField === 'Username' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">VPN Password</label>
                <div className="flex items-center space-x-2">
                  <Input
                    readOnly
                    value={viewedCredentials.vpn_password}
                    className="font-mono text-sm"
                  />
                  <Button
                    size="sm"
                    onClick={() => copyToClipboard(viewedCredentials.vpn_password, 'Password')}
                    className="bg-blue-500 hover:bg-blue-600"
                  >
                    {copiedField === 'Password' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">RADIUS Secret</label>
                <div className="flex items-center space-x-2">
                  <Input
                    readOnly
                    value={viewedCredentials.radius_secret || 'testing123'}
                    className="font-mono text-sm bg-yellow-50 dark:bg-yellow-900/30"
                  />
                  <Button
                    size="sm"
                    onClick={() => copyToClipboard(viewedCredentials.radius_secret || 'testing123', 'RADIUS Secret')}
                    className="bg-blue-500 hover:bg-blue-600"
                  >
                    {copiedField === 'RADIUS Secret' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">Secret ini digunakan untuk konfigurasi RADIUS di MikroTik</p>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">MikroTik Setup Script</label>
                <div className="relative">
                  <textarea
                    readOnly
                    value={viewedCredentials.mikrotik_script || `# ========================================
# PANDUAN SETUP RADIUS VIA VPN (L2TP)
# Tenant: ${viewedCredentials.tenant_name || 'N/A'}
# Untuk: PPPoE + Hotspot
# ========================================

# ----------------------------------------
# LANGKAH 1: Buat L2TP Client
# ----------------------------------------
/interface l2tp-client add \\
    name="vpn-radius" \\
    connect-to=${viewedCredentials.vpn_server_ip || '103.179.252.149'} \\
    user="${viewedCredentials.vpn_username}" \\
    password="${viewedCredentials.vpn_password}" \\
    use-ipsec=no \\
    add-default-route=no \\
    disabled=no \\
    comment="VPN to RADIUS Server"

# ----------------------------------------
# LANGKAH 2: Verifikasi VPN Connected
# ----------------------------------------
:delay 3s
/interface l2tp-client print where name="vpn-radius"
# Status harus: R (Running)

# ----------------------------------------
# LANGKAH 3: Test Ping ke RADIUS Server
# ----------------------------------------
/ping 10.10.10.1 count=3
# Harus ada reply dari 10.10.10.1

# ----------------------------------------
# LANGKAH 4: Setup RADIUS (PPPoE + Hotspot)
# ----------------------------------------
/radius remove [find where address!=10.10.10.1]
/radius add \\
    service=ppp,hotspot \\
    address=10.10.10.1 \\
    secret=${viewedCredentials.radius_secret || 'testing123'} \\
    timeout=3s \\
    comment="RADIUS via VPN Tunnel"
/radius print

# ----------------------------------------
# LANGKAH 5: Aktifkan RADIUS di PPP
# ----------------------------------------
/ppp profile set default use-radius=yes

# ----------------------------------------
# LANGKAH 6: Aktifkan RADIUS di Hotspot
# ----------------------------------------
/ip hotspot profile set default use-radius=yes

# ----------------------------------------
# SELESAI! Test PPPoE dan Hotspot client
# ----------------------------------------
`}
                    className="w-full h-96 p-3 border border-slate-300 dark:border-slate-600 rounded-md bg-slate-900 text-green-400 font-mono text-xs"
                  />
                  <Button
                    size="sm"
                    onClick={() => {
                      const script = viewedCredentials.mikrotik_script || `# ========================================
# PANDUAN SETUP RADIUS VIA VPN (L2TP)
# Tenant: ${viewedCredentials.tenant_name || 'N/A'}
# Untuk: PPPoE + Hotspot
# ========================================

# ----------------------------------------
# LANGKAH 1: Buat L2TP Client
# ----------------------------------------
/interface l2tp-client add \\
    name="vpn-radius" \\
    connect-to=${viewedCredentials.vpn_server_ip || '103.179.252.149'} \\
    user="${viewedCredentials.vpn_username}" \\
    password="${viewedCredentials.vpn_password}" \\
    use-ipsec=no \\
    add-default-route=no \\
    disabled=no \\
    comment="VPN to RADIUS Server"

# ----------------------------------------
# LANGKAH 2: Verifikasi VPN Connected
# ----------------------------------------
:delay 3s
/interface l2tp-client print where name="vpn-radius"
# Status harus: R (Running)

# ----------------------------------------
# LANGKAH 3: Test Ping ke RADIUS Server
# ----------------------------------------
/ping 10.10.10.1 count=3
# Harus ada reply dari 10.10.10.1

# ----------------------------------------
# LANGKAH 4: Setup RADIUS (PPPoE + Hotspot)
# ----------------------------------------
/radius remove [find where address!=10.10.10.1]
/radius add \\
    service=ppp,hotspot \\
    address=10.10.10.1 \\
    secret=${viewedCredentials.radius_secret || 'testing123'} \\
    timeout=3s \\
    comment="RADIUS via VPN Tunnel"
/radius print

# ----------------------------------------
# LANGKAH 5: Aktifkan RADIUS di PPP
# ----------------------------------------
/ppp profile set default use-radius=yes

# ----------------------------------------
# LANGKAH 6: Aktifkan RADIUS di Hotspot
# ----------------------------------------
/ip hotspot profile set default use-radius=yes

# ----------------------------------------
# SELESAI! Test PPPoE dan Hotspot client
# ----------------------------------------
`;
                      copyToClipboard(script, 'Script');
                    }}
                    className="absolute top-2 right-2 bg-blue-500 hover:bg-blue-600"
                  >
                    {copiedField === 'Script' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              <div className="pt-2 border-t">
                <Button
                  onClick={() => {
                    setShowViewModal(false);
                    setViewedCredentials(null);
                  }}
                  className="w-full bg-slate-600 hover:bg-slate-700"
                >
                  Close
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      {/* === Overlap Modal (subnet route conflict) === */}
      <Dialog open={!!overlapModal} onOpenChange={(o) => !o && setOverlapModal(null)}>
        <DialogContent className="max-w-2xl" data-testid="overlap-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Subnet Overlap dengan Router Lain
            </DialogTitle>
            <DialogDescription>
              VPS hanya dapat me-route SATU router untuk 1 subnet pada satu waktu.
              Kalau Anda "Timpa", router-router di bawah ini akan <b>KEHILANGAN route</b>
              dari VPS untuk subnet yang tumpang tindih. User di router lama akan
              tidak bisa diakses via IP publik / NAT sampai admin migrasi subnet.
            </DialogDescription>
          </DialogHeader>

          {overlapModal && (
            <div className="space-y-3">
              <div className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-3 text-sm">
                <div className="text-slate-500 dark:text-slate-400">Anda akan set VPN</div>
                <div className="font-mono font-semibold text-slate-900 dark:text-slate-100 mt-0.5" data-testid="overlap-target-vpn">
                  {overlapModal.vpnUsername}
                </div>
                <div className="text-slate-500 dark:text-slate-400 mt-2">ONT subnet baru</div>
                <div className="font-mono text-xs bg-white dark:bg-slate-900 rounded px-2 py-1 mt-1 border border-slate-200 dark:border-slate-700 break-all">
                  {overlapModal.newSubnet || '(kosong)'}
                </div>
              </div>

              <div>
                <div className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                  Router yang akan kehilangan route:
                </div>
                <div className="max-h-60 overflow-y-auto rounded-lg border border-red-200 dark:border-red-900/40">
                  <table className="w-full text-sm">
                    <thead className="bg-red-50 dark:bg-red-900/20 text-slate-700 dark:text-slate-200 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">VPN</th>
                        <th className="text-left px-3 py-2 font-medium">Router</th>
                        <th className="text-left px-3 py-2 font-medium">Subnet</th>
                        <th className="text-left px-3 py-2 font-medium">Peer IP</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-red-100 dark:divide-red-900/30" data-testid="overlap-conflicts-body">
                      {(overlapModal.conflicts || []).map((c, i) => (
                        <tr key={i} className="hover:bg-red-50/60 dark:hover:bg-red-900/10">
                          <td className="px-3 py-2 font-mono text-xs">{c.username}</td>
                          <td className="px-3 py-2">{c.router_name || '-'}</td>
                          <td className="px-3 py-2 font-mono text-xs text-red-700 dark:text-red-300">{c.subnet}</td>
                          <td className="px-3 py-2 font-mono text-xs">{c.peer_ip || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900/40 rounded-lg p-3 text-xs text-amber-900 dark:text-amber-200">
                <b>Rekomendasi</b>: Kalau Anda TIDAK sengaja overlap, klik <b>Batal</b> dan pilih subnet BEBAS
                (mis. via Subnet Planner). Kalau memang mau migrasi router → force timpa DAN sesudahnya
                pindahkan subnet baru untuk router-router yang di atas supaya mereka jalan lagi.
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2 sm:justify-end">
            <Button
              variant="outline"
              onClick={() => setOverlapModal(null)}
              disabled={overlapForcing}
              data-testid="overlap-cancel-btn"
            >
              Batal
            </Button>
            <Button
              onClick={forceOverlapUpdate}
              disabled={overlapForcing}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="overlap-force-btn"
            >
              {overlapForcing ? 'Memproses...' : 'Force Timpa (router lama kehilangan route)'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantVPNManager;
